import { CSSProperties, useCallback } from 'react';
import {
  Field,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  ButtonVariantType,
  ComponentProps,
  DynamicHeading,
  isColorBright,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import NavButton from 'composables/shared/NavButton/NavButton';
import { ResponsiveImage } from 'composables/shared/ResponsiveImage/ResponsiveImage';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

type EnterpriseGenericPromoSpotLayout = 'middle' | 'right' | 'left' | 'bottom' | 'top' | '';
type EnterpriseGenericPromoSpotDecorativeTextPosition = 'top' | 'bottom' | '';
type EnterpriseGenericPromoSpotDecorativeTextAlignment = 'left' | 'right' | 'center' | '';

export type EnterpriseGenericPromoSpotProps = {
  fields: EnterpriseGenericPromoSpotRendering;
} & ComponentProps;

export type EnterpriseGenericPromoSpotRendering = {
  eyebrowText: TextField;
  title: TextField;
  secondaryTitle?: TextField;
  description: TextField;
  decorativeText?: TextField;
  decorativeTextColor?: TextField;
  decorativeTextSize?: TextField;
  decorativeTextPos?: Field<EnterpriseGenericPromoSpotDecorativeTextPosition>;
  decorativeTextAlign?: Field<EnterpriseGenericPromoSpotDecorativeTextAlignment>;
  backgroundColor: TextField;
  orientation: Field<EnterpriseGenericPromoSpotLayout>;
  firstCtaButtonText: TextField;
  firstCtaButtonAriaLabel?: Field<string>;
  firstCtaButtonVariant: Field<ButtonVariantType>;
  firstCtaButtonLink: LinkField;
  secondCtaButtonText: TextField;
  secondCtaButtonAriaLabel?: Field<string>;
  secondCtaButtonVariant: Field<ButtonVariantType>;
  secondCtaButtonLink: LinkField;
  desktopImage: ImageField;
  mobileImage: ImageField;
  imageLink: LinkField;
  imageLinkAriaLabel?: Field<string>;
  imageBackgroundGradient: TextField;
  fontColor: TextField;
  roundedImage: Field<boolean>;
  firstCtaButtonIcon?: Field<keyof typeof icons>;
  secondCtaButtonIcon?: Field<keyof typeof icons>;
  isMainHeading: Field<boolean>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
  trackingSpotName?: Field<string>;
};

const CONTAINER_CLASSES = {
  base: 'flex justify-center w-full container-fluid gap-8 relative [container-type:inline-size]',
  layouts: {
    middle: '',
    right: '',
    left: '',
    bottom: 'flex-col',
    top: 'flex-col-reverse',
  },
} as const;

const LAYOUT_CLASSES = {
  base: 'container flex items-center gap-8 py-0 relative z-10 ',
  layouts: {
    middle: 'flex-col text-center',
    right: 'flex-col-reverse md:flex-row',
    left: 'flex-col-reverse md:flex-row-reverse',
    bottom: 'flex-col-reverse text-center',
    top: 'flex-col text-center',
  },
} as const;

const CONTENT_CLASSES = {
  base: 'flex flex-col gap-8',
  layouts: {
    middle: 'items-center max-w-6xl',
    right: 'w-full md:w-1/2',
    left: 'w-full md:w-1/2',
    bottom: 'items-center max-w-6xl',
    top: 'items-center max-w-6xl',
  },
} as const;

const IMAGE_CLASSES = {
  base: 'flex justify-center align-middle',
  layouts: {
    middle: 'py-6',
    right: 'w-full md:w-1/2 rounded-xl',
    left: 'w-full md:w-1/2 rounded-xl',
    bottom: 'w-full',
    top: 'w-full',
  },
} as const;

const EnterpriseGenericPromoSpot = ({ fields }: EnterpriseGenericPromoSpotProps) => {
  const {
    eyebrowText,
    desktopImage,
    mobileImage,
    title,
    secondaryTitle,
    description,
    decorativeText,
    decorativeTextColor,
    decorativeTextSize,
    decorativeTextPos,
    decorativeTextAlign,
    backgroundColor,
    imageBackgroundGradient,
    orientation,
    firstCtaButtonText,
    firstCtaButtonAriaLabel,
    secondCtaButtonText,
    secondCtaButtonAriaLabel,
    firstCtaButtonVariant,
    secondCtaButtonVariant,
    firstCtaButtonLink,
    secondCtaButtonLink,
    imageLink,
    imageLinkAriaLabel,
    fontColor,
    roundedImage,
    firstCtaButtonIcon,
    secondCtaButtonIcon,
    isMainHeading,
    firstCtaButtonTrackingName,
    secondCtaButtonTrackingName,
    trackingSpotName,
  } = fields;
  const backgroundColorHex = resolveColorProperty(backgroundColor?.value as string) ?? '#ffffff';
  const fontColorHex = fontColor?.value
    ? resolveColorProperty(fontColor.value as string)
    : undefined;
  const decorativeTextColorHex =
    resolveColorProperty(decorativeTextColor?.value as string) ?? '#000000';

  const isBackgroundColorBright = isColorBright(backgroundColorHex);

  const backgroundTextColorClass = isBackgroundColorBright ? 'light' : 'dark';

  const spotRef = useSpotTracking(trackingSpotName?.value);

  const hasImage = desktopImage?.value?.src || mobileImage?.value?.src;

  const spotContainerStyles: CSSProperties = {
    backgroundColor: backgroundColorHex,
  };

  const imageBackgroundGradientStyle: CSSProperties = {
    background: imageBackgroundGradient?.value,
  };

  const imageOrientation = !orientation?.value ? 'bottom' : orientation.value;
  const decorativeTextPosition = decorativeTextPos?.value === 'bottom' ? 'bottom' : 'top';
  const decorativeTextAlignment: 'left' | 'center' | 'right' =
    decorativeTextAlign?.value === 'center'
      ? 'center'
      : decorativeTextAlign?.value === 'right'
        ? 'right'
        : 'left';

  const decorativeTextPosClass =
    decorativeTextPosition === 'bottom'
      ? 'bottom-0 translate-y-[0.10em]'
      : 'top-0 -translate-y-[0.10em]';
  const decorativeTextAlignClass =
    decorativeTextAlignment === 'center'
      ? 'left-1/2 -translate-x-1/2 text-center'
      : decorativeTextAlignment === 'right'
        ? 'right-0 translate-x-[0.10em] text-right'
        : 'left-0 -translate-x-[0.10em] text-left';

  const commonProps = {
    style: spotContainerStyles,
    className: classNames(
      CONTAINER_CLASSES.base,
      CONTAINER_CLASSES.layouts[imageOrientation],
      backgroundTextColorClass
    ),
  };

  const renderImage = useCallback(
    () =>
      imageLink?.value.href ? (
        <a
          className="block focus-visible:outline-focus"
          href={imageLink.value.href}
          {...(imageLinkAriaLabel?.value && {
            'aria-label': imageLinkAriaLabel.value,
          })}
          {...(imageLink.value?.target && {
            target: imageLink.value.target,
            rel: 'noopener noreferrer',
          })}
        >
          <ResponsiveImage
            desktopImage={desktopImage}
            mobileImage={mobileImage}
            rounded={roundedImage?.value}
          />
        </a>
      ) : (
        <ResponsiveImage
          desktopImage={desktopImage}
          mobileImage={mobileImage}
          rounded={roundedImage?.value}
        />
      ),
    [imageLink, desktopImage, mobileImage, roundedImage, imageLinkAriaLabel]
  );

  const SecondaryHeadingTag = isMainHeading?.value ? 'h2' : 'h3';

  return (
    <div {...commonProps} ref={spotRef} data-wt-spot={trackingSpotName?.value}>
      {decorativeText?.value && (
        <div
          className={classNames(
            'pointer-events-none absolute z-0 m-0 p-0 font-black leading-[0.85] supports-[font-size:1cqw]:text-[10cqw]',
            decorativeTextPosClass,
            decorativeTextAlignClass
          )}
          aria-hidden="true"
          style={{ color: decorativeTextColorHex, fontSize: decorativeTextSize?.value }}
        >
          {decorativeText.value}
        </div>
      )}
      <div className={classNames(LAYOUT_CLASSES.base, LAYOUT_CLASSES.layouts[imageOrientation])}>
        <div
          className={classNames(CONTENT_CLASSES.base, CONTENT_CLASSES.layouts[imageOrientation])}
        >
          <div>
            <Eyebrow
              className={classNames(
                'mb-4',
                ['left', 'right'].includes(orientation?.value) &&
                  'max-md:items-center max-md:text-center'
              )}
              align={['top', 'bottom', 'middle'].includes(imageOrientation) ? 'center' : 'left'}
              text={eyebrowText?.value as string}
            />
            {title?.value && (
              <DynamicHeading
                className={classNames(
                  isMainHeading?.value ? 'heading-1' : 'heading-2',
                  'max-md:text-center'
                )}
                style={{ color: fontColorHex }}
                isMainHeading={isMainHeading?.value}
                htmlContent={sanitize(title.value as string)}
              />
            )}
            {secondaryTitle?.value && (
              <SecondaryHeadingTag
                className="heading-4 max-md:text-center"
                style={{ color: fontColorHex }}
                dangerouslySetInnerHTML={{ __html: sanitize(secondaryTitle.value as string) }}
              />
            )}

            {imageOrientation === 'middle' && hasImage && (
              <div
                className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[imageOrientation])}
              >
                {renderImage()}
              </div>
            )}
            {description?.value && (
              <p
                className="paragraph-large max-md:text-center"
                style={{ color: fontColorHex }}
                dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-4 max-md:justify-center">
            {!!firstCtaButtonLink?.value?.href && (
              <NavButton
                variant={firstCtaButtonVariant.value || 'primary'}
                size="m"
                link={firstCtaButtonLink}
                icon={firstCtaButtonIcon?.value}
                title={firstCtaButtonText.value as string}
                ariaLabel={firstCtaButtonAriaLabel?.value}
                {...(firstCtaButtonTrackingName?.value && {
                  'data-wt-name': firstCtaButtonTrackingName.value,
                })}
              />
            )}
            {!!secondCtaButtonLink?.value?.href && (
              <NavButton
                variant={secondCtaButtonVariant.value || 'primary'}
                size="m"
                link={secondCtaButtonLink}
                icon={secondCtaButtonIcon?.value}
                title={secondCtaButtonText.value as string}
                ariaLabel={secondCtaButtonAriaLabel?.value}
                {...(secondCtaButtonTrackingName?.value && {
                  'data-wt-name': secondCtaButtonTrackingName.value,
                })}
              />
            )}
          </div>
        </div>
        {hasImage && !['top', 'bottom', 'middle'].includes(imageOrientation) && (
          <div className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[imageOrientation])}>
            {renderImage()}
          </div>
        )}
      </div>
      {hasImage && ['top', 'bottom'].includes(imageOrientation) && (
        <div style={imageBackgroundGradientStyle}>
          <div className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[imageOrientation])}>
            {renderImage()}
          </div>
        </div>
      )}
    </div>
  );
};

export default withDatasourceCheck()(EnterpriseGenericPromoSpot);
